import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Send, Upload, X, ImageIcon } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = { name: "", company: "", email: "", phone: "", machine: "", part: "", message: "", website: "" };
const MAX_FILES = 6;

export const DelerForm = () => {
  const [form, setForm] = useState(EMPTY);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const addFiles = (e) => {
    const incoming = Array.from(e.target.files || []).filter((f) => f.type.startsWith("image/"));
    setFiles((prev) => {
      const merged = [...prev, ...incoming].slice(0, MAX_FILES);
      if (prev.length + incoming.length > MAX_FILES) toast.error(`Maks ${MAX_FILES} bilder.`);
      return merged;
    });
    e.target.value = "";
  };

  const removeFile = (idx) => setFiles((prev) => prev.filter((_, i) => i !== idx));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.machine || !form.part) {
      toast.error("Fyll ut navn, e-post, telefon, maskin og ønsket del.");
      return;
    }
    setLoading(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      files.forEach((f) => fd.append("images", f));
      const { data } = await axios.post(`${API}/kontakt/deler`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(data.message || "Takk! Delebestillingen er mottatt.");
      setForm(EMPTY);
      setFiles([]);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Noe gikk galt. Prøv igjen.");
    } finally {
      setLoading(false);
    }
  };

  const field = "w-full bg-transparent border-0 border-b-2 border-ink/20 focus:border-redx rounded-none px-0 py-3 text-ink placeholder:text-ink/30 outline-none transition-colors duration-200";
  const lbl = "font-oswald uppercase text-xs tracking-[0.2em] text-ink/50 mb-1 block";

  return (
    <form onSubmit={submit} className="space-y-7" data-testid="contact-form-deler">
      {/* Honeypot — hidden from real users, traps bots */}
      <div aria-hidden="true" style={{ position: "absolute", left: "-9999px", width: 1, height: 1, overflow: "hidden" }}>
        <label>Ikke fyll ut dette feltet
          <input type="text" tabIndex={-1} autoComplete="off" value={form.website} onChange={set("website")} data-testid="deler-hp" />
        </label>
      </div>
      <div className="grid sm:grid-cols-2 gap-7">
        <div>
          <label className={lbl}>Navn *</label>
          <input className={field} value={form.name} onChange={set("name")} placeholder="Ditt navn" data-testid="deler-input-name" />
        </div>
        <div>
          <label className={lbl}>Bedrift</label>
          <input className={field} value={form.company} onChange={set("company")} placeholder="Bedriftsnavn" data-testid="deler-input-company" />
        </div>
        <div>
          <label className={lbl}>E-post *</label>
          <input type="email" className={field} value={form.email} onChange={set("email")} placeholder="deg@bedrift.no" data-testid="deler-input-email" />
        </div>
        <div>
          <label className={lbl}>Telefon *</label>
          <input className={field} value={form.phone} onChange={set("phone")} placeholder="000 00 000" data-testid="deler-input-phone" />
        </div>
        <div>
          <label className={lbl}>Maskin / modell *</label>
          <input className={field} value={form.machine} onChange={set("machine")} placeholder="F.eks. Electrolux green&clean" data-testid="deler-input-machine" />
        </div>
        <div>
          <label className={lbl}>Ønsket del *</label>
          <input className={field} value={form.part} onChange={set("part")} placeholder="Hvilken del trenger du?" data-testid="deler-input-part" />
        </div>
      </div>

      <div>
        <label className={lbl}>Tilleggsinfo</label>
        <textarea
          rows={3}
          className={`${field} resize-none`}
          value={form.message}
          onChange={set("message")}
          placeholder="Beskriv problemet eller delen nærmere..."
          data-testid="deler-input-message"
        />
      </div>

      {/* Image upload */}
      <div>
        <label className={lbl}>Bilder av merkeskilt (anbefalt)</label>
        <label
          htmlFor="deler-files"
          className="mt-2 flex items-center gap-3 border-2 border-dashed border-ink/25 hover:border-redx px-5 py-6 cursor-pointer transition-colors duration-200"
          data-testid="deler-upload-dropzone"
        >
          <Upload size={20} className="text-redx" />
          <span className="text-ink/60 text-sm">
            Last opp bilde av merkeskiltet på maskinen — dette gjør det enklere å finne riktig del. (maks {MAX_FILES} bilder, 5 MB hver)
          </span>
          <input id="deler-files" type="file" accept="image/*" multiple className="hidden" onChange={addFiles} data-testid="deler-input-files" />
        </label>

        {files.length > 0 && (
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-3 mt-4" data-testid="deler-file-previews">
            {files.map((f, i) => (
              <div key={i} className="relative group aspect-square border border-ink/15 overflow-hidden bg-bone">
                <img src={URL.createObjectURL(f)} alt={f.name} className="w-full h-full object-cover" />
                <button
                  type="button"
                  onClick={() => removeFile(i)}
                  data-testid={`deler-remove-file-${i}`}
                  className="absolute top-1 right-1 bg-ink/80 text-white p-1 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-redx"
                  aria-label="Fjern bilde"
                >
                  <X size={14} />
                </button>
              </div>
            ))}
          </div>
        )}
        {files.length === 0 && (
          <p className="flex items-center gap-2 text-ink/40 text-xs mt-3">
            <ImageIcon size={14} /> Ingen bilder valgt ennå
          </p>
        )}
      </div>

      <button
        type="submit"
        disabled={loading}
        data-testid="deler-submit-button"
        className="group w-full sm:w-auto flex items-center justify-center gap-3 bg-ink text-white px-8 py-4 font-oswald uppercase tracking-widest text-sm hover:bg-redx transition-colors duration-200 disabled:opacity-60"
      >
        {loading ? <Loader2 size={18} className="animate-spin" /> : <Send size={17} />}
        {loading ? "Sender..." : "Send delebestilling"}
      </button>
    </form>
  );
};
